import CustomButton from "@components/common/Button";
import { capitalizeText } from "@lib/capitalizeText";
import { ImportRecord } from "@/utils/types/importManagement";
import { useQuery } from "@tanstack/react-query";
import { Alert, Descriptions, Statistic, Tag, Typography } from "antd";
import { CheckCircle, Download, FileText, XCircle } from "lucide-react";
import { TenantUserService } from "../../../../services/APIs";

const { Text } = Typography;

interface ImportSummaryProps {
  importDetail: ImportRecord;
  onDownloadErrorFile: () => void;
}

const ImportSummary = ({
  importDetail,
  onDownloadErrorFile,
}: ImportSummaryProps) => {
  const fileNameParts = importDetail.file_name.split("/");
  const actualFileName = fileNameParts[fileNameParts.length - 1];

  const renderStatistics = () => (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <div>
        <Statistic
          title="Total Rows"
          value={importDetail.total_rows}
          valueStyle={{ color: "#1890ff" }}
          prefix={<FileText size={16} />}
        />
      </div>
      <div>
        <Statistic
          title="Successfully Imported"
          value={importDetail.created_count}
          valueStyle={{ color: "#52c41a" }}
          prefix={<CheckCircle size={16} />}
        />
      </div>
      <div>
        <Statistic
          title="Updated"
          value={importDetail.updated_count}
          valueStyle={{ color: "#faad14" }}
        />
      </div>
      <div>
        <Statistic
          title="Failed"
          value={importDetail.failed_count}
          valueStyle={{ color: "#ff4d4f" }}
          prefix={<XCircle size={16} />}
        />
      </div>
    </div>
  );

  const renderDetails = () => (
    <Descriptions title="Import Details" bordered size="small">
      <Descriptions.Item label="Schema" span={2}>
        <Tag color="blue">{capitalizeText(importDetail.schema_name)}</Tag>
      </Descriptions.Item>
      <Descriptions.Item label="File Name" span={2}>
        {actualFileName}
      </Descriptions.Item>
      <Descriptions.Item label="Import Type" span={2}>
        {importDetail.import_type}
      </Descriptions.Item>
      <Descriptions.Item label="Created On" span={2}>
        {new Date(importDetail.created_on).toLocaleString()}
      </Descriptions.Item>
      <Descriptions.Item label="Created By" span={2}>
        {user?.user.first_name} {user?.user.last_name}
      </Descriptions.Item>
    </Descriptions>
  );

  const { data: user } = useQuery({
    queryKey: ["tenantUser", importDetail.created_by],
    queryFn: () =>
      TenantUserService.getTenantUserById(importDetail.created_by ?? ""),
    enabled: !!importDetail.created_by,
  });

  if (importDetail.status === "Success") {
    return (
      <div className="space-y-6">
        <Alert
          type="success"
          message="Import Completed Successfully"
          description={`Successfully imported ${importDetail.created_count.toLocaleString()} records into ${capitalizeText(importDetail.schema_name)}`}
          showIcon
          icon={<CheckCircle />}
        />

        {renderStatistics()}
        {renderDetails()}
      </div>
    );
  }

  if (importDetail.status === "Failed") {
    return (
      <div className="space-y-6">
        <Alert
          type="error"
          message="Import Failed"
          description={`${importDetail.failed_count.toLocaleString()} records failed to import. Please check the error details.`}
          showIcon
          icon={<XCircle />}
          action={
            importDetail.error_file && (
              <CustomButton
                size="small"
                danger
                icon={<Download size={14} />}
                onClick={onDownloadErrorFile}
                title="Download Error Details File"
              >
                Download Error File
              </CustomButton>
            )
          }
        />

        {renderStatistics()}

        <Descriptions title="Import Details" bordered size="small">
          <Descriptions.Item label="Schema" span={2}>
            {capitalizeText(importDetail.schema_name)}
          </Descriptions.Item>
          <Descriptions.Item label="File Name" span={2}>
            {actualFileName}
          </Descriptions.Item>
          <Descriptions.Item label="Import Type" span={2}>
            {importDetail.import_type}
          </Descriptions.Item>
          <Descriptions.Item label="Created On" span={2}>
            {new Date(importDetail.created_on).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Created By" span={2}>
            {user?.user.first_name} {user?.user.last_name}
          </Descriptions.Item>
          <Descriptions.Item label="Error File" span={2}>
            {importDetail.error_file ? (
              <CustomButton
                type="link"
                icon={<Download size={14} />}
                onClick={onDownloadErrorFile}
                title="Download Error Details File"
              >
                Download Error File
              </CustomButton>
            ) : (
              <Text type="secondary">No error file available</Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </div>
    );
  }

  return null;
};

export default ImportSummary;
